// src/components/PaymentStep.js
import React, { useState } from 'react';
import { Lock, AlertCircle, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ✅ Charger Stripe avec la clé publique
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const DEMO_MODE = false;

const paymentTranslations = {
  fr: {
    title: 'Sécurisation de la réservation',
    paymentSummary: 'Récapitulatif de paiement',
    client: 'Client :',
    totalAmount: 'Montant total du séjour :',
    preAuthRequested: 'Pré-autorisation demandée :',
    noActualCharge: 'Sans débit effectif',
    automaticCharge: 'Débit automatique :',
    chargeDate: '35 jours avant l\'arrivée',
    bankNotice: 'Votre banque va probablement afficher 0€ ou 1€. C\'est normal ! Nous vérifions juste que votre carte est valide.',
    securePayment: 'Paiement sécurisé par Stripe',
    secureInfo: 'Vos données bancaires sont cryptées et ne sont jamais stockées sur nos serveurs',
    cardName: 'Nom sur la carte',
    cardDetails: 'Informations de carte bancaire',
    noteTitle: 'Note :',
    noteText: 'Votre carte sera pré-autorisée pour {amount}€ mais ne sera débitée qu\'après confirmation ou 35 jours avant la date d\'arrivée. La pré-autorisation sera automatiquement annulée en cas de non confirmation de la réservation.',
    back: 'Retour',
    processing: 'Traitement...',
    preAuthorize: 'Pré-autoriser',
    errorDeclined: 'Carte refusée. Veuillez vérifier vos informations ou utiliser une autre carte.',
    errorInsufficient: 'Fonds insuffisants sur la carte.',
    errorIncorrectCvc: 'Code CVC incorrect.',
    errorExpired: 'Carte expirée.',
    errorConnection: 'Erreur de connexion. Veuillez réessayer.',
    errorGeneric: 'Erreur lors de la vérification de la carte. Veuillez réessayer.',
    errorIncomplete: 'Veuillez remplir toutes les informations de carte.'
  },
  en: {
    title: 'Securing the reservation',
    paymentSummary: 'Payment summary',
    client: 'Client:',
    totalAmount: 'Total stay amount:',
    preAuthRequested: 'Pre-authorization requested:',
    noActualCharge: 'No actual charge',
    automaticCharge: 'Automatic charge:',
    chargeDate: '35 days before arrival',
    bankNotice: 'Your bank will probably display €0 or €1. This is normal! We are just verifying that your card is valid.',
    securePayment: 'Secure payment by Stripe',
    secureInfo: 'Your banking data is encrypted and never stored on our servers',
    cardName: 'Name on card',
    cardDetails: 'Card information',
    noteTitle: 'Note:',
    noteText: 'Your card will be pre-authorized for €{amount} but will only be charged after confirmation or 35 days before arrival date. The pre-authorization will be automatically cancelled if the reservation is not confirmed.',
    back: 'Back',
    processing: 'Processing...',
    preAuthorize: 'Pre-authorize',
    errorDeclined: 'Card declined. Please check your information or use another card.',
    errorInsufficient: 'Insufficient funds on the card.',
    errorIncorrectCvc: 'Incorrect CVC code.',
    errorExpired: 'Card expired.',
    errorConnection: 'Connection error. Please try again.',
    errorGeneric: 'Error verifying the card. Please try again.',
    errorIncomplete: 'Please fill in all card information.'
  }
};

// Composant interne avec accès à Stripe
function PaymentForm({ 
  amount, 
  onPaymentSuccess, 
  onPaymentError,
  onBack,
  guestInfo,
  reservationData,
  language,
  pt
}) {
  const stripe = useStripe();
  const elements = useElements();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardName, setCardName] = useState(`${guestInfo.firstName} ${guestInfo.name}`);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    if (DEMO_MODE) {
      setTimeout(() => {
        console.log('🎭 Mode démo : Paiement simulé');
        onPaymentSuccess({
          id: 'demo_payment_' + Date.now(),
          amount: amount * 100,
          status: 'succeeded',
          last4: '4242'
        });
        // ✅ NE PAS remettre processing à false après succès (même en mode démo)
      }, 2000);
      return;
    }



try {
  const cardElement = elements.getElement(CardElement);

  // 🔵 ÉTAPE 1 : Créer un SetupIntent sur le backend
  const apiUrl = process.env.REACT_APP_API_URL || 'https://valetier.maisonceronne.com/api';
  const setupResponse = await fetch(`${apiUrl}/valetier/create-setup-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerEmail: guestInfo.email,
      customerName: cardName,
      reservationId: reservationData?.id || Date.now().toString()
    })
  });

  const setupData = await setupResponse.json();

  if (!setupData.success) {
    console.error('❌ Erreur création SetupIntent:', setupData.error);
    setError(setupData.error || pt.errorGeneric);
    if (onPaymentError) onPaymentError();
    setProcessing(false);
    return;
  }

  console.log('✅ SetupIntent créé, confirmation...');

  // 🔵 ÉTAPE 2 : Confirmer le SetupIntent (déclenchera 3D Secure si nécessaire)
  const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(setupData.clientSecret, {
    payment_method: {
      card: cardElement,
      billing_details: {
        name: cardName,
        email: guestInfo.email
      }
    }
  });

  if (confirmError) {
    console.error('❌ Erreur confirmation SetupIntent:', confirmError);
    
    if (confirmError.code === 'card_declined') {
      setError(pt.errorDeclined);
    } else if (confirmError.code === 'insufficient_funds') {
      setError(pt.errorInsufficient);
    } else if (confirmError.code === 'incorrect_cvc') {
      setError(pt.errorIncorrectCvc);
    } else if (confirmError.code === 'expired_card') {
      setError(pt.errorExpired);
    } else if (confirmError.code === 'incomplete_card' || confirmError.type === 'validation_error') {
      setError(pt.errorIncomplete);
    } else {
      setError(confirmError.message || pt.errorGeneric);
    }
    
    if (onPaymentError) onPaymentError();
    setProcessing(false);
    return;
  }


console.log('✅ SetupIntent confirmé:', setupIntent.id);

// 🔵 ÉTAPE 3 : Récupérer les détails de la carte depuis le backend
const pmResponse = await fetch('https://valetier.maisonceronne.com/api/valetier/get-payment-method', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    paymentMethodId: setupIntent.payment_method
  })
});

const pmData = await pmResponse.json();

if (!pmData.success) {
  console.error('❌ Erreur récupération PaymentMethod:', pmData.error);
  setError(pt.errorGeneric);
  if (onPaymentError) onPaymentError();
  setProcessing(false);
  return;
}

onPaymentSuccess({
  stripeCustomerId: setupData.customerId,
  stripePaymentMethodId: setupIntent.payment_method,
  cardholderName: cardName,
  last4: pmData.last4,
  brand: pmData.brand,
  expiryMonth: pmData.exp_month,
  expiryYear: pmData.exp_year,
  status: 'authorized',
  amount: amount * 100
});

// ✅ NE PAS remettre processing à false après succès
// Le composant sera démonté quand la réservation sera confirmée
// Cela évite que le bouton redevienne cliquable pendant le traitement

}
    catch (error) {
      console.error('❌ Erreur:', error);
      setError(pt.errorConnection);
      
      if (onPaymentError) {
        onPaymentError();
      }
      
      // ✅ Remettre processing à false UNIQUEMENT en cas d'erreur
      setProcessing(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-8">
      <h2 className="text-2xl font-light mb-6">{pt.title}</h2>
      
 <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-2">{pt.paymentSummary}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>{pt.client}</span>
            <strong>{guestInfo.firstName} {guestInfo.name}</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.totalAmount}</span>
            <strong>{amount}€</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.preAuthRequested}</span>
            <strong className="text-green-600">{pt.noActualCharge}</strong>
          </div>
          <div className="flex justify-between">
            <span>{pt.automaticCharge}</span>
            <strong>{pt.chargeDate}</strong>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-300">
          <p className="text-xs text-gray-600">
            {pt.bankNotice}
          </p>
        </div>
      </div>


      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-green-600" />
            <span className="font-semibold">{pt.securePayment}</span>
          </div>
          <p className="text-sm text-gray-600">
            {pt.secureInfo}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              {pt.cardName}
            </label>
            <input
              type="text"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              placeholder={language === 'fr' ? "Jean Dupont" : "John Doe"}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {pt.cardDetails}
            </label>
            
<div className="border rounded-lg p-2 bg-white" style={{ minHeight: '70px' }}>
                <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#000',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    },
                    invalid: {
                      color: '#EF4444',
                    },
                  },
                  hidePostalCode: true,
layout: 'auto'
                }}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-gray-50 border border-black rounded-lg p-4">
          <p className="text-sm text-black">
            <strong>{pt.noteTitle}</strong> {pt.noteText.replace('{amount}', amount)}
          </p>
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {pt.back}
          </button>
          <button
            type="submit"
            disabled={processing || !stripe}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 
                       disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {pt.processing}
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                {pt.preAuthorize} {amount}€
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Composant principal avec le provider Stripe Elements
export default function PaymentStep(props) {
  const pt = paymentTranslations[props.language || 'fr'] || paymentTranslations.fr;

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} pt={pt} />
    </Elements>
  );
}